import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { FieldArray, formValueSelector } from 'redux-form';

import type FieldWorkClassificationCodeListItemVM from 'ab-viewModels/fieldWorkClassificationCode/listItem';
import type OccupiedSlotsForWorkOrderVM from 'ab-viewModels/timeSheet/occupiedSlotsForWorkOrder.viewModel';
import type TimeSplitEquipmentVM from 'ab-viewModels/timeSplitEquipment/timeSplitEquipment';
import type CondensedTableVM from 'ab-viewModels/workOrder/workOrderForReportsCondensedTable';

import type { TimeSplitEntryRM, TimeSheetEntryRM } from 'ab-requestModels/timeSheet/timeSheetUpdate';

import * as TimeUtils from '@acceligentllc/shared/utils/time';
import { shouldEnableFeatureFromDate } from '@acceligentllc/shared/utils/timeSheet';
import type { OverlapMeta } from '@acceligentllc/shared/utils/timeSheetEntry';
import { findOverlapsByVirtualId } from '@acceligentllc/shared/utils/timeSheetEntry';

import { TimeSheetInternalApprovalStatus } from '@acceligentllc/shared/enums/timeSheetApprovalStatus';
import type { WorkOrderReviewStatus } from '@acceligentllc/shared/enums/workOrderReviewStatus';

import { DEFAULT_TIME_DURATION_MINUTE_ROUNDING_INTERVAL } from '@acceligentllc/shared/constants/value';

import type { RootState } from 'af-reducers';

import { useToggle } from 'af-utils/react.util';

import * as FORMS from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import Tooltip from 'af-components/Tooltip';

import type FormModel from './formModel';
import { sumTotalEquipmentTime, sumTotalJobTime } from './helpers';
import ReviewCell from './ReviewCell';
import SignatureStatus from './SignatureStatus';
import TimeSheetArray from './TimeSheetArray';
import Equipment from './TimeSplitArray';
import CertificationStatusDisplay from '../../FieldReportsList/TimeSheets/TimeSheetRow/CertificationStatusDisplay';
import RejectModal from '../../Shared/RejectTimeSheetModal';

interface EntryForOverlapCheck {
	/** format: ISO_DATETIME */
	startTime: string;
	/** format: ISO_DATETIME */
	endTime: Nullable<string>;
	virtualId: string;
}

interface OwnProps {
	hasPermissionToEditTime: boolean;
	canEditEquipment: boolean;
	workOrderId: number;
	workRequestId: number;
	data: CondensedTableVM['employees'][0];
	classificationCodeList: FieldWorkClassificationCodeListItemVM[];
	equipment: TimeSplitEquipmentVM;
	dueDate: string;
	occupiedSlots: OccupiedSlotsForWorkOrderVM;
	updateTimeSheet: (accountId: number, form: TimeSheetEntryRM[]) => void;
	updateEquipment: (accountId: number, form: TimeSplitEntryRM[]) => void;
	/** value: ISO_DATETIME */
	change: (fieldName: string, value: string) => void;
	onReject: () => void;
	onEditStart: (accountId: number) => void;
	onEditEnd: (accountId: number) => void;
	isTemporaryEmployee: boolean;
	reviewStatus: WorkOrderReviewStatus;
	renderCertStatus?: boolean;
	isRejectActionAvailable: boolean;
	isRemovedFromWorkOrder: boolean;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const renderError = (error: string, index: number) => <span key={index}>{error}</span>;
const renderTooltip = (errors: string[]) => <div className="condensed-table__employee-list-employee__error-list">{errors.map(renderError)}</div>;

const CondensedRowEmployee: React.FC<Props> = (props) => {
	const {
		data,
		dueDate,
		isAllowedToEditTimeSheet,
		canEditEquipment,
		workOrderId,
		workRequestId,
		classificationCodeList,
		equipment,
		updateEquipment,
		updateTimeSheet,
		change,
		onEditEnd,
		onEditStart,
		timeSheetEntries,
		timeSplitEntries,
		occupiedSlots,
		isTemporaryEmployee,
		shouldRoundTo1Min,
		renderCertStatus,
		isRejectActionAvailable,
		isRemovedFromWorkOrder,
	} = props;

	const {
		value: showRejectModal,
		setToTrue: openRejectModal,
		setToFalse: closeRejectModal,
	} = useToggle(false);

	const {
		value: editSheet,
		setToTrue: _startEditingSheet,
		setToFalse: _endEditingSheet,
	} = useToggle(false);

	const {
		value: editEquipment,
		setToTrue: _startEditingEquipment,
		setToFalse: _endEditingEquipment,
	} = useToggle(false);

	const [hasOverlap, setHasOverlap] = React.useState(false);
	const [overlap, setOverlap] = React.useState<Record<string, OverlapMeta>>({});

	const startEditingEquipment = React.useCallback(() => {
		_startEditingEquipment();
		onEditStart(data.accountId);
	}, [_startEditingEquipment, onEditStart, data]);

	const endEditingEquipment = React.useCallback(() => {
		_endEditingEquipment();
		if (!editSheet) {
			onEditEnd(data.accountId);
		}
	}, [_endEditingEquipment, onEditEnd, data, editSheet]);

	const startEditingSheet = React.useCallback(() => {
		_startEditingSheet();
		onEditStart(data.accountId);
	}, [_startEditingSheet, onEditStart, data]);

	const endEditingSheet = React.useCallback(() => {
		_endEditingSheet();
		if (!editEquipment) {
			onEditEnd(data.accountId);
		}
	}, [_endEditingSheet, onEditEnd, editEquipment, data]);

	React.useEffect(() => {
		const formattedEntries = timeSheetEntries.reduce<EntryForOverlapCheck[]>((_acc, _e, _index) => {
			if (!!_e.startTime) {
				_acc.push({
					startTime: _e.startTime,
					endTime: _e.endTime,
					virtualId: _e.id?.toString() ?? `created_${_index}`,
				});
			}
			return _acc;
		}, []);
		const overlaps = findOverlapsByVirtualId(formattedEntries, occupiedSlots?.slots);

		const _hasOverlap = Object.keys(overlaps).some((_key) => (overlaps[_key].startTime || overlaps[_key].endTime || overlaps[_key].occupied));
		setHasOverlap(_hasOverlap);
		setOverlap(overlaps);

	}, [occupiedSlots, timeSheetEntries]);

	const timeSheetEntriesName = `workOrderMap.${workOrderId}.${data.accountId}.timeSheetEntries`;
	const timeSplitEntriesName = `workOrderMap.${workOrderId}.${data.accountId}.timeSplitEntries`;

	const handleUpdateEquipment = React.useCallback((form: TimeSplitEntryRM[]) => {
		updateEquipment(data.accountId, form);
	}, [data, updateEquipment]);

	const handleUpdateSheet = React.useCallback((form: TimeSheetEntryRM[]) => {
		updateTimeSheet(data.accountId, form);
	}, [data, updateTimeSheet]);

	const totalJobTime = React.useMemo(() => {
		const sum = timeSheetEntries.reduce(sumTotalJobTime, 0);
		return shouldRoundTo1Min
			? sum
			: TimeUtils.roundTimeDurationToInterval(sum, DEFAULT_TIME_DURATION_MINUTE_ROUNDING_INTERVAL);
	}, [timeSheetEntries, shouldRoundTo1Min]);

	const totalEquipmentTime = React.useMemo(() => {
		return timeSplitEntries.reduce(sumTotalEquipmentTime, 0);
	}, [timeSplitEntries]);

	const errors = React.useMemo(() => {
		const result: string[] = [];
		if (hasOverlap) {
			result.push('One or more corrections overlap with activities on the timeline');
		}
		if (totalEquipmentTime > totalJobTime) {
			result.push('Total equipment time exceeds total job time');
		}
		return result.length ? renderTooltip(result) : null;
	}, [totalJobTime, totalEquipmentTime, hasOverlap]);

	return (
		<div className="condensed-table__employee-list-item">
			<div className={renderCertStatus ? 'condensed-table__employee-list-employee-with-cert' : 'condensed-table__employee-list-employee'}>
				<div className="condensed-table__employee-list-employee__error-indicator">
					{!!errors && (
						<Tooltip message={errors} placement="right">
							<span className="icon-warning" />
						</Tooltip>
					)}
				</div>
				<div className="condensed-table__employee-list-employee__name">
					{
						isTemporaryEmployee &&
						<span className="icon-temp_labor condensed-table__temp-labor-icon" />
					}
					{
						isRemovedFromWorkOrder &&
						<Tooltip message="Removed from work order" placement="top">
							<span className="icon-warning condensed-table__removed-icon" />
						</Tooltip>
					}
					<div className="bold">
						{data.fullName}
					</div>

				</div>
				{isRejectActionAvailable ? (
					<Button
						customStyleClass="condensed-table__employee-list-employee__reject"
						label="Reject Time Sheet"
						onClick={openRejectModal}
						size="small"
						style="danger"
					/>
				) : (
					<div />
				)}
				<SignatureStatus signatureStatus={isTemporaryEmployee ? null : data.signatureStatus} />
				{renderCertStatus &&
					<div className="cert-status-container">
						{
							data?.workShifts.map((_shift) => {
								return (
									<CertificationStatusDisplay
										key={`${_shift.accountId}:${_shift.startTime}-${_shift.endTime}`}
										tooltipPlacement={'top'}
										workShift={_shift}
									/>
								);
							})
						}
					</div>
				}
				<div className="condensed-table__employee-list-employee__review-status">
					<ReviewCell
						approvalSignature={data.approvalSignatureUrl}
						approvedAt={data.approvedAt}
						approvedBy={data.approvedBy}
						rejectedAt={data.rejectedAt}
						rejectedBy={data.rejectedBy}
						rejectReason={data.rejectReason}
						status={data.approvalStatus}
					/>
				</div>
			</div>
			<FieldArray
				canEdit={isAllowedToEditTimeSheet}
				change={change}
				component={TimeSheetArray}
				dueDate={dueDate}
				endEditing={endEditingSheet}
				inEditMode={editSheet}
				name={timeSheetEntriesName}
				overlap={overlap}
				startEditing={startEditingSheet}
				update={handleUpdateSheet}
			/>
			<FieldArray
				canEdit={canEditEquipment}
				classificationCodeList={classificationCodeList}
				component={Equipment}
				endEditing={endEditingEquipment}
				equipment={equipment}
				inEditMode={editEquipment}
				name={timeSplitEntriesName}
				shouldRoundTimeIntervalsTo1Min={shouldRoundTo1Min}
				startEditing={startEditingEquipment}
				timeAvailableForEquipment={Math.max(0, totalJobTime - totalEquipmentTime)}
				update={handleUpdateEquipment}
				workOrderId={workOrderId}
				workRequestId={workRequestId}
			/>
			<RejectModal
				accountId={data.accountId}
				close={closeRejectModal}
				code="code"
				dueDate={dueDate}
				employeeFullName={data.fullName}
				showModal={showRejectModal}
				workOrderId={workOrderId}
			/>
		</div>
	);
};

function mapStateToProps(state: RootState, props: OwnProps) {
	const { data, workOrderId, hasPermissionToEditTime, dueDate } = props;
	const { company } = state.company;

	if (!company) {
		throw new Error('company is required in this context');
	}

	const workOrderMap: FormModel['workOrderMap'] = formValueSelector(FORMS.CONDENSED_TABLE_EMPLOYEE)(state, 'workOrderMap');
	const accountData = workOrderMap?.[workOrderId]?.[data.accountId];
	const sheetApproved = data.approvalStatus === TimeSheetInternalApprovalStatus.APPROVED;

	return {
		timeSheetEntries: accountData?.timeSheetEntries ?? [],
		timeSplitEntries: accountData?.timeSplitEntries ?? [],
		isAllowedToEditTimeSheet: !sheetApproved && company.isFRManageAllowedToEditTimeSheet && hasPermissionToEditTime,
		shouldRoundTo1Min: shouldEnableFeatureFromDate({ enableFeatureFrom: company.timeRoundingPolicyFrom, workOrderDueDate: dueDate }),
	};
}

const connector = connect(mapStateToProps);

export default connector(CondensedRowEmployee);
